import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CommodityForOverride,
  PlatformLocationConfiguration,
  UpdateLocationConfigurationRequest,
} from 'src/api/microservices/platform-markets-location-config'
import {
  GetCommodityOverrideConfigurables,
  GetCommoditiesForOverride,
  GetLocationConfigurables,
  GetLocationConfigurations,
  UpdateLocationConfiguration,
} from 'src/api/microservices/platform-markets-location-config'
import { useCompanyContext } from 'src/companies/routes/company-context'

export const PLATFORM_MARKETS_LOCATION_CONFIGURABLES_QUERY_KEY =
  'platform-markets-location-configurables'
export const PLATFORM_MARKETS_COMMODITY_OVERRIDE_CONFIGURABLES_QUERY_KEY =
  'platform-markets-commodity-override-configurables'
export const PLATFORM_MARKETS_COMMODITIES_FOR_OVERRIDE_QUERY_KEY =
  'platform-markets-commodities-for-override'
export const PLATFORM_MARKETS_LOCATION_CONFIGURATIONS_QUERY_KEY =
  'platform-markets-location-configurations'
export const PLATFORM_MARKETS_UPDATE_LOCATION_CONFIGURATION_MUTATION_KEY =
  'platform-markets-update-location-configuration'

export function useGetLocationConfigurables() {
  const {
    company: { tenant_id: tenantId },
  } = useCompanyContext()

  return useQuery<TenantConfigResponse[]>({
    queryKey: [PLATFORM_MARKETS_LOCATION_CONFIGURABLES_QUERY_KEY, { tenantId }],
    queryFn: async () => {
      const [err, response] = await GetLocationConfigurables(tenantId)
      if (err) throw err
      return response.configurables
    },
  })
}

export function useGetCommodityOverrideConfigurables() {
  const {
    company: { tenant_id: tenantId },
  } = useCompanyContext()

  return useQuery<TenantConfigResponse[]>({
    queryKey: [PLATFORM_MARKETS_COMMODITY_OVERRIDE_CONFIGURABLES_QUERY_KEY, { tenantId }],
    queryFn: async () => {
      const [err, response] = await GetCommodityOverrideConfigurables(tenantId)
      if (err) throw err
      return response.configurables
    },
  })
}

export function useGetCommoditiesForOverride(datasourceProviderId?: string) {
  const {
    company: { tenant_id: tenantId },
  } = useCompanyContext()

  return useQuery<CommodityForOverride[]>({
    queryKey: [
      PLATFORM_MARKETS_COMMODITIES_FOR_OVERRIDE_QUERY_KEY,
      { tenantId, datasourceProviderId },
    ],
    queryFn: async () => {
      const [err, response] = await GetCommoditiesForOverride({ datasourceProviderId }, tenantId)
      if (err) throw err
      return response.data
    },
    enabled: !!datasourceProviderId,
  })
}

export function useGetLocationConfigurations() {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()

  return useQuery<PlatformLocationConfiguration[]>({
    queryKey: [PLATFORM_MARKETS_LOCATION_CONFIGURATIONS_QUERY_KEY, { tenantId, datasourceId }],
    queryFn: async () => {
      const [err, response] = await GetLocationConfigurations({ datasourceId }, tenantId)
      if (err) throw err
      return response.data
    },
    enabled: !!datasourceId,
  })
}

export function useUpdateLocationConfiguration() {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()

  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [
      PLATFORM_MARKETS_UPDATE_LOCATION_CONFIGURATION_MUTATION_KEY,
      { tenantId, datasourceId },
    ],
    mutationFn: async (data: UpdateLocationConfigurationRequest) => {
      const [err, response] = await UpdateLocationConfiguration(data, tenantId)
      if (err) throw err

      await queryClient.invalidateQueries({
        queryKey: [PLATFORM_MARKETS_LOCATION_CONFIGURATIONS_QUERY_KEY],
      })

      return response
    },
  })
}
